import { HeatmapLayer } from '@deck.gl/aggregation-layers';

const EASE_OUT_QUAD = (t: number) => t * (2 - t);
const LAYER_TRANSITION = { duration: 800, easing: EASE_OUT_QUAD };

const HEATMAP_MIN_ZOOM = 4;
const HEATMAP_MAX_ZOOM = 20;
const HEATMAP_MIN_RADIUS = 15;
const HEATMAP_MAX_RADIUS = 100;

/** A GeoJSON-like point the heatmap reads its position from, as `[lng, lat]`. */
export interface HeatmapFeature {
  geometry: { coordinates: number[] };
  properties?: Record<string, unknown>;
}

interface CreateHeatmapLayerOptions<T extends HeatmapFeature> {
  id: string;
  data: T[];
  /** Map zoom; callers debounce it so the radius doesn't churn while zooming. */
  zoom: number;
  getWeight: (feature: T) => number;
}

/**
 * Heatmap blob radius in pixels for a map zoom: grows linearly from
 * `HEATMAP_MIN_RADIUS` at zoom 4 to `HEATMAP_MAX_RADIUS` at zoom 20.
 */
export function heatmapRadiusForZoom(zoom: number): number {
  if (zoom >= HEATMAP_MAX_ZOOM) return HEATMAP_MAX_RADIUS;
  if (zoom <= HEATMAP_MIN_ZOOM) return HEATMAP_MIN_RADIUS;
  return (
    HEATMAP_MIN_RADIUS +
    ((HEATMAP_MAX_RADIUS - HEATMAP_MIN_RADIUS) * (zoom - HEATMAP_MIN_ZOOM)) /
      (HEATMAP_MAX_ZOOM - HEATMAP_MIN_ZOOM)
  );
}

/**
 * Builds the deck.gl heatmap layer shared by Discovery and Scout. Render it
 * through `MedMapDeckOverlay`, and build it in `useMemo`.
 */
export function createHeatmapLayer<T extends HeatmapFeature>({
  id,
  data,
  zoom,
  getWeight,
}: CreateHeatmapLayerOptions<T>) {
  return new HeatmapLayer<T>({
    id,
    data,
    aggregation: 'SUM',
    getPosition: (d) => d.geometry.coordinates as [number, number],
    getWeight,
    radiusPixels: heatmapRadiusForZoom(zoom),
    intensity: 5,
    threshold: 0.03,
    opacity: 0.6,
    transitions: {
      getPosition: LAYER_TRANSITION,
      getWeight: LAYER_TRANSITION,
      radiusPixels: LAYER_TRANSITION,
      opacity: LAYER_TRANSITION,
    },
  });
}
